import { cn } from '@/utils'
import { dragGhostPosition } from './helpers/drag-ghost-position'
import type { PlacementDragGhostProps } from './types/placement-drag-ghost-props'

/**
 * The floating thumbnail that follows the pointer while a tray item (photo, ClikArt) is dragged
 * onto the canvas. `PhotoDragGhost` and `ClikArtDragGhost` are thin wrappers that map their own
 * item type onto `imageUrl`/`label`.
 */
export function PlacementDragGhost({
  imageUrl,
  label,
  clientX,
  clientY,
  width,
  height,
  opacity,
  overCanvas,
  overFillTarget,
  keyboardCarry,
  flyback = false,
  thumbOrigin,
}: PlacementDragGhostProps) {
  const { left, top } = dragGhostPosition({
    clientX,
    clientY,
    width,
    height,
    keyboardCarry,
    flyback,
    thumbOrigin,
  })

  return (
    <div
      className={cn(
        'pointer-events-none fixed z-[9999] rounded-lg border-2 shadow-lg',
        flyback
          ? 'transition-all duration-200 ease-out'
          : 'transition-[border-color,box-shadow] duration-100',
        overFillTarget ? 'border-green-600' : overCanvas ? 'border-primary/60' : 'border-border'
      )}
      style={{
        left,
        top,
        width,
        height,
        opacity,
        transform: flyback ? 'scale(1)' : undefined,
        boxShadow: overFillTarget ? '0 0 12px rgba(22,163,74,0.5)' : undefined,
      }}
      aria-hidden
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt=""
          className="h-full w-full rounded-lg object-cover"
          draggable={false}
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center rounded-lg bg-muted text-xs text-muted-foreground">
          {label}
        </div>
      )}
    </div>
  )
}
